import type { FunctionComponent, SVGProps } from "react"
import { ArrowLeft } from "lucide-react"

import AwarenessIcon from "@/assets/img/aura/insight-awareness.svg?react"
import IntuitionIcon from "@/assets/img/aura/insight-intuition.svg?react"
import PerceptionIcon from "@/assets/img/aura/insight-perception.svg?react"
import ReceptivityIcon from "@/assets/img/aura/insight-receptivity.svg?react"
import SensitivityIcon from "@/assets/img/aura/insight-sensitivity.svg?react"
import SubtletyIcon from "@/assets/img/aura/insight-subtlety.svg?react"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

type SvgIcon = FunctionComponent<SVGProps<SVGSVGElement>>

const INSIGHTS: Record<
  string,
  {
    accentWord: string
    traits: { label: string; Icon: SvgIcon; tone: string }[]
  }
> = {
  "intuition-clearly": {
    accentWord: "CLEARLY",
    traits: [
      { label: "Perception", Icon: PerceptionIcon, tone: "bg-peach/20 text-peach" },
      { label: "Awareness", Icon: AwarenessIcon, tone: "bg-gold/20 text-gold" },
      { label: "Intuition", Icon: IntuitionIcon, tone: "bg-cyan/20 text-cyan" },
    ],
  },
  "intuition-softly": {
    accentWord: "SOFTLY",
    traits: [
      { label: "Sensitivity", Icon: SensitivityIcon, tone: "bg-peach/20 text-peach" },
      { label: "Receptivity", Icon: ReceptivityIcon, tone: "bg-gold/20 text-gold" },
      { label: "Subtlety", Icon: SubtletyIcon, tone: "bg-cyan/20 text-cyan" },
    ],
  },
}

/**
 * Aura intuition interstitial (Step15.1 / 15.2): two-line title with accent
 * word, hero, and three tinted trait cards overlaid on the art.
 */
export function AuraInsightStep({ step, onAdvance, onBack }: StepComponentProps) {
  const meta = INSIGHTS[step.id]
  const accent = meta?.accentWord

  return (
    <div
      className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-dark-base text-purple-light"
      data-slot="aura-insight-step"
    >
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="space-y-2 px-4 text-center">
          <h1 className="flex flex-col items-center font-display text-2xl font-medium uppercase">
            <span className="text-purple-light">Your intuition speaks</span>
            {accent && (
              <span className="text-main-purple">
                {accent}
              </span>
            )}
          </h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="relative mx-auto h-[378px] w-full max-w-[500px] shrink-0">
            {step.coverImage && (
              <img
                src={step.coverImage}
                alt=""
                className="absolute top-0 left-1/2 h-[378px] w-[345px] max-w-none -translate-x-1/2 object-contain"
              />
            )}
            {meta && (
              <div className="absolute inset-x-0 bottom-0 flex gap-3 px-4">
                {meta.traits.map(({ label, Icon, tone }) => (
                  <div
                    key={label}
                    className={cn(
                      "flex h-[68px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl backdrop-blur-[6px]",
                      tone,
                    )}
                  >
                    <Icon className="size-8" />
                    <span className="text-xs font-semibold uppercase">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-4 pt-2 pb-safe">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
